import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Zap, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      setJoined(true);
      toast.success("You're on the list");
    } catch {
      toast.error("Could not subscribe — please retry.");
    } finally {
      setLoading(false);
    }
  };

  const go = (e, href) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.__lenis?.scrollTo(href, { offset: -70 });
    } else {
      navigate("/" + href);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative border-t border-border bg-forest">
      <div className="energy-stripes h-3 w-full" />
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-3 lg:px-8">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="clip-corner-sm flex h-9 w-9 items-center justify-center bg-brass">
              <Zap className="h-5 w-5 text-white" strokeWidth={2.25} fill="currentColor" />
            </span>
            <span className="font-serif text-lg font-black uppercase leading-none tracking-wide text-bone">
              Off-Road Electric
              <span className="block font-mono text-[9px] font-medium tracking-[0.42em] text-cyanx">
                Bikes
              </span>
            </span>
          </div>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-sand">
            The UK's home for electric bikes — road-legal HitWay, plus off-road
            Sur-Ron, Talaria and OSET. Real photos, plain facts, one honest price
            with UK delivery included.
          </p>
          <div className="disclaimer-box mt-5 flex max-w-xs items-start gap-2 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brass" />
            <p className="font-mono text-[9px] font-medium leading-relaxed tracking-[0.05em] text-bone/85 uppercase">
              Off-road bikes are for private land use only — not road legal. Road-legal range: EN 15194 certified.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
          <div>
            <p className="mono-label">Ride</p>
            <ul className="mt-4 space-y-2.5">
              {[
                ["Off-Road", "#off-road"],
                ["Road Legal", "#road-legal"],
                ["Accessories", "#accessories"],
                ["Deals", "#deals"],
                ["Contact", "#contact"],
              ].map(([label, href]) => (
                <li key={label}>
                  <a
                    href={href}
                    data-testid={`footer-link-${label.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={(e) => go(e, href)}
                    className="text-sm text-sand transition-colors hover:text-brass"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mono-label">Talk</p>
            <ul className="mt-4 space-y-2.5 text-sm text-sand">
              <li>
                <a href="tel:07522364950" data-testid="footer-phone" className="transition-colors hover:text-brass">
                  07522 364950
                </a>
              </li>
              <li>
                <a href="mailto:ecobikecampsupp@gmail.com" data-testid="footer-email" className="break-all transition-colors hover:text-brass">
                  ecobikecampsupp@gmail.com
                </a>
              </li>
            </ul>
          </div>
          <div>
            <p className="mono-label">Legal</p>
            <ul className="mt-4 space-y-2.5">
              {[
                ["Terms & Conditions", "/terms", "terms"],
                ["Privacy Policy", "/privacy", "privacy"],
                ["Returns & Refunds", "/returns", "returns"],
              ].map(([label, to, key]) => (
                <li key={key}>
                  <Link
                    to={to}
                    data-testid={`footer-link-${key}`}
                    className="text-sm text-sand transition-colors hover:text-brass"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div>
          <p className="font-serif text-2xl font-black uppercase text-bone">
            Drop alerts &amp; <span className="text-volt">deals</span>
          </p>
          <p className="mt-2 text-sm leading-relaxed text-sand">
            New stock slots, bundle offers and OSET restocks — first to know.
          </p>
          {joined ? (
            <p data-testid="newsletter-success" className="mt-5 flex items-center gap-2 text-sm text-volt">
              <CheckCircle2 className="h-4 w-4" />
              You're in. Watch your inbox.
            </p>
          ) : (
            <form data-testid="newsletter-form" onSubmit={submit} className="mt-5 flex">
              <input
                data-testid="newsletter-email-input"
                required
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-border bg-forest-deep/70 px-4 py-3 text-sm text-bone placeholder:text-sand/40 outline-none transition-colors focus:border-brass"
              />
              <button
                data-testid="newsletter-submit-button"
                disabled={loading}
                className="flex shrink-0 items-center gap-2 bg-brass px-5 font-mono text-[10px] font-bold tracking-[0.15em] text-white uppercase transition-colors hover:bg-brass-bright disabled:opacity-50"
              >
                Join
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </form>
          )}
        </div>
      </div>
      <div className="border-t border-border py-6 text-center font-mono text-[10px] tracking-[0.18em] text-sand/40 uppercase">
        © {new Date().getFullYear()} Off-Road Electric Bikes · offroadelectricbikes.co.uk · Real photos, real facts, one honest price · Ride safe, ride lawful
      </div>
    </footer>
  );
};
